import React, { useState, useEffect } from "react";
import { collection, getDocs, doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { UserProfile } from "../../types";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { MagnifyingGlassIcon } from "@heroicons/react/24/solid";

const ApprovedUsers: React.FC = () => {
  const [allApprovedUsers, setAllApprovedUsers] = useState<UserProfile[]>([]);
  const [displayUsers, setDisplayUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Fisher-Yates shuffle function to randomize the array
  const shuffleArray = (array: UserProfile[]): UserProfile[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  useEffect(() => {
    const fetchApprovedUsers = async () => {
      try {
        setLoading(true);

        // First, get all document IDs from the 'approved' collection
        // These are the UIDs of approved users
        const approvedRef = collection(db, "approved");
        const approvedSnapshot = await getDocs(approvedRef);
        const approvedUids: string[] = [];

        approvedSnapshot.forEach((approvedDoc) => {
          // The document ID in the approved collection is the user's UID
          approvedUids.push(approvedDoc.id);
        });

        // Next, fetch user details for each approved UID from the users collection
        const users: UserProfile[] = [];

        // Use Promise.all to fetch all user data in parallel
        await Promise.all(
          approvedUids.map(async (uid) => {
            const userDocRef = doc(db, "users", uid);
            const userDocSnap = await getDoc(userDocRef);

            if (userDocSnap.exists() && userDocSnap.data().displayName) {
              // Add user data to our array, ensuring the uid is included
              const userData = userDocSnap.data() as UserProfile;
              users.push({
                ...userData,
                uid: uid,
              });
            }
          })
        );

        // Store all users and set initial display to shuffled first 8
        setAllApprovedUsers(users);
        const shuffledUsers = shuffleArray(users);
        setDisplayUsers(shuffledUsers.slice(0, 8));
      } catch (err: any) {
        console.error("Error fetching approved users:", err);
        setError("Failed to load approved users");
      } finally {
        setLoading(false);
      }
    };

    fetchApprovedUsers();
  }, []);

  // Update display users when search query changes
  useEffect(() => {
    if (!searchQuery.trim()) {
      // If no search query, show shuffled first 8 users
      const shuffledUsers = shuffleArray(allApprovedUsers);
      setDisplayUsers(shuffledUsers.slice(0, 8));
    } else {
      // If searching, filter through all users
      const query = searchQuery.toLowerCase().trim();
      const filteredUsers = allApprovedUsers.filter((user) => {
        // Name matching - check if query is included in the display name
        const nameMatch = user.displayName?.toLowerCase().includes(query);

        // Sport matching - check for partial matches in the sport field
        const sportMatch = user.sport
          ? user.sport.toLowerCase().includes(query)
          : false;

        // Description matching - check for partial matches in the description
        const descriptionMatch = user.description
          ? user.description.toLowerCase().includes(query)
          : false;

        return nameMatch || sportMatch || descriptionMatch;
      });
      setDisplayUsers(filteredUsers);
    }
  }, [searchQuery, allApprovedUsers]);

  const handleUserClick = (e: React.MouseEvent, user: UserProfile) => {
    e.preventDefault(); // Prevent the default anchor tag behavior
    setSelectedUser(user);
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setSelectedUser(null);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center p-4">{error}</div>;
  }

  if (allApprovedUsers.length === 0) {
    return (
      <div className="text-gray-500 italic text-center p-4">
        No approved athletes at the moment.
      </div>
    );
  }

  return (
    <>
      {/* Search input */}
      <div className="relative max-w-xl mx-auto mb-16">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gold" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-3 py-4 border border-gold rounded-md leading-5 bg-goldlight placeholder-gray-500 focus:outline-none focus:ring-gold focus:border-gold sm:text-sm"
          placeholder="Filter by name or sport"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Show "no results" message if searching and no matches found */}
      {displayUsers.length === 0 && searchQuery.trim() && (
        <div className="text-gray-500 italic text-center p-4">
          No athletes found matching "{searchQuery}"
        </div>
      )}

      {/* Show athletes grid if there are users to display */}
      {displayUsers.length > 0 && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-8">
            {displayUsers.map((user) => (
              <a
                key={user.uid}
                href={user.socialMediaLink || "#"}
                onClick={(e) => handleUserClick(e, user)}
                className="group cursor-pointer"
              >
                <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300 transform group-hover:scale-105 group-hover:shadow-lg">
                  <div className="aspect-square bg-gray-200 relative">
                    {user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt={user.displayName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-300">
                        <span className="text-gray-600 text-4xl">
                          {user.displayName.charAt(0)}
                        </span>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                      <div className="p-4 text-white">
                        <p className="text-sm">View profile</p>
                      </div>
                    </div>

                    {/* Display sponsor logo if available */}
                    {user.sponsorLogoURL && (
                      <div className="absolute bottom-2 right-2 w-12 h-12 sm:w-16 sm:h-16 bg-white p-1 rounded shadow-md">
                        <img
                          src={user.sponsorLogoURL}
                          alt="Sponsor"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}

                    {/* Display "Open to sponsorship" badge */}
                    {user.openToSponsorship && (
                      <div className="absolute top-3 left-3 bg-gold px-2 py-1 rounded-xl text-xs text-white font-semibold shadow-sm max-w-[calc(100%-80px)] truncate">
                        Open to sponsorship
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-gray-900 truncate">
                      {user.displayName}
                    </h3>
                    {user.bio && (
                      <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                        {user.bio}
                      </p>
                    )}
                    {user.sponsorName && (
                      <p className="text-gold text-xs mt-1">
                        Sponsored by {user.sponsorName}
                      </p>
                    )}
                  </div>
                </div>
              </a>
            ))}
          </div>
          <div className="mt-16 flex justify-center">
            <a
              href="/athletes"
              className="rounded-xl bg-goldlight px-3.5 py-2.5 text-sm font-semibold text-gold shadow-xs hover:text-white hover:bg-gold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
            >
              View All Athletes
            </a>
          </div>
        </>
      )}
      <Transition show={isDialogOpen} as={React.Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeDialog}>
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={React.Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                  <div className="absolute right-4 top-4">
                    <button
                      type="button"
                      className="text-gray-400 hover:text-gray-500"
                      onClick={closeDialog}
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>

                  {selectedUser && (
                    <div>
                      <div className="flex items-center mb-6">
                        <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden mr-4 relative flex-shrink-0">
                          {selectedUser.photoURL ? (
                            <img
                              src={selectedUser.photoURL}
                              alt={selectedUser.displayName}
                              className="w-full h-full object-cover aspect-square"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-300 aspect-square">
                              <span className="text-gray-600 text-2xl sm:text-4xl">
                                {selectedUser.displayName.charAt(0)}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex flex-col justify-center">
                          <Dialog.Title
                            as="h3"
                            className="text-2xl font-bold text-gray-900"
                          >
                            {selectedUser.displayName}
                          </Dialog.Title>

                          {selectedUser.openToSponsorship &&
                            !selectedUser.sponsorName && (
                              <p className="text-xs text-gold bg-goldlight rounded-md px-2 py-1 mt-2 inline-block w-fit">
                                Open to sponsorship opportunities
                              </p>
                            )}
                        </div>
                      </div>

                      {selectedUser.bio && (
                        <div className="mb-6">
                          <p className="text-gray-600 font-bold">
                            {selectedUser.bio}
                          </p>
                        </div>
                      )}

                      {selectedUser.description && (
                        <div className="mb-6">
                          <p className="text-gray-600 text-sm">
                            {selectedUser.description}
                          </p>
                        </div>
                      )}

                      {selectedUser.sport && (
                        <div className="">
                          <p className="text-gray-600">
                            Sport: <strong>{selectedUser.sport}</strong>
                          </p>
                        </div>
                      )}
                      {selectedUser.location && (
                        <div className="">
                          <p className="text-gray-600">
                            Location: <strong>{selectedUser.location}</strong>
                          </p>
                        </div>
                      )}

                      {/* Display sponsor logo in dialog if available */}
                      {selectedUser.sponsorLogoURL && (
                        <div className="mb-6 mt-6">
                          <h4 className="text-lg font-semibold mb-2">
                            Sponsor
                          </h4>
                          <div className="w-32 h-32 p-2 border rounded">
                            <img
                              src={selectedUser.sponsorLogoURL}
                              alt="Sponsor Logo"
                              className="w-full h-full object-contain"
                            />
                          </div>
                        </div>
                      )}

                      {selectedUser.socialMediaLink && (
                        <div className="mt-6">
                          <a
                            href={selectedUser.socialMediaLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-gold hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                          >
                            Learn more about
                            <strong className="ml-1">
                              {" "}
                              {selectedUser.displayName}
                            </strong>
                          </a>
                        </div>
                      )}
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  );
};

export default ApprovedUsers;
